import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { ShieldCheck, Rocket, BadgePercent } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const CONSENT_VERSION = "2026-08-02.v1";
const SKIP_PATHS = ["/terms", "/privacy", "/business", "/pogopedia", "/admin", "/pricing", "/about", "/features", "/comics", "/showcase", "/validate", "/heaven", "/pitch", "/lanes"];

const anonId = () => {
  let id = localStorage.getItem("airpog_anon_id");
  if (!id) {
    id = "anon_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem("airpog_anon_id", id);
  }
  return id;
};

export const recordConsent = (intent) =>
  axios.post(`${API}/consent`, { anon_id: anonId(), intent }, { withCredentials: true }).catch(() => {});

export default function ConsentGate() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => {
    if (SKIP_PATHS.some((p) => window.location.pathname.startsWith(p))) return false;
    try { return localStorage.getItem("airpog_consent_v") !== CONSENT_VERSION; } catch { return true; }
  });
  const [agreed, setAgreed] = useState(false);
  const [shake, setShake] = useState(false);
  const [hint, setHint] = useState(false);
  const [gateDeals, setGateDeals] = useState([]);
  // 👋 Return-visit signup ladder: visit 2 = dismissible reminder, visit 3+ = mandatory wall
  const [joinPrompt, setJoinPrompt] = useState(null); // null | "soft" | "hard"
  const wasPogr = (() => { try { return localStorage.getItem("airpog_was_pogr") === "1"; } catch { return false; } })();

  useEffect(() => {
    if (!sessionStorage.getItem("airpog_session_counted")) {
      const v = parseInt(localStorage.getItem("airpog_visit_count") || "1", 10) + 1;
      localStorage.setItem("airpog_visit_count", String(v));
      sessionStorage.setItem("airpog_session_counted", "1");
    }
    let disposed = false;
    const evaluate = async (delay) => {
      if (SKIP_PATHS.some((p) => window.location.pathname.startsWith(p))) { setJoinPrompt(null); return; }
      if (localStorage.getItem("airpog_consent_v") !== CONSENT_VERSION) return; // consent gate handles it
      const visits = parseInt(localStorage.getItem("airpog_visit_count") || "1", 10);
      if (visits < 2) return; // still on the free look
      await new Promise((res) => setTimeout(res, delay));
      if (disposed) return;
      try {
        const r = await axios.get(`${API}/consumer/auth/me`, { withCredentials: true });
        if (r.data?.user_id || r.data?.email) { setJoinPrompt(null); return; } // signed-in POG'R
      } catch { /* not signed in */ }
      if (disposed) return;
      setJoinPrompt("hard"); // one free look — after that, signup is mandatory. No back-button escapes.
      if (!sessionStorage.getItem("airpog_wall_logged")) {
        sessionStorage.setItem("airpog_wall_logged", "1");
        axios.post(`${API}/funnel`, { event: "wall_shown", anon_id: localStorage.getItem("airpog_anon_id") }).catch(() => {});
      }
    };
    evaluate(2500);
    const recheck = () => evaluate(400);
    const onVisible = () => { if (document.visibilityState === "visible") recheck(); };
    window.addEventListener("airpog:auth-changed", recheck);
    window.addEventListener("pageshow", recheck);
    window.addEventListener("popstate", recheck);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      disposed = true;
      window.removeEventListener("airpog:auth-changed", recheck);
      window.removeEventListener("pageshow", recheck);
      window.removeEventListener("popstate", recheck);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [open, pathname]);

  const goSignup = () => {
    recordConsent(wasPogr ? "signin_return" : "signup_return");
    axios.post(`${API}/funnel`, { event: "wall_signup_click", anon_id: localStorage.getItem("airpog_anon_id") }).catch(() => {});
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
  };

  const accept = (intent) => {
    try {
      localStorage.setItem("airpog_consent_v", CONSENT_VERSION);
      localStorage.setItem("airpog_visit_count", "1");
      sessionStorage.setItem("airpog_session_counted", "1");
    } catch { /* private mode */ }
    recordConsent(intent);
    if (intent === "signup") {
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/")}`;
      return;
    }
    setOpen(false);
  };

  // Buttons stay clickable — clicking without consent shakes the checkbox instead of a dead button.
  const tryAccept = (intent) => {
    if (!agreed) {
      setHint(true);
      setShake(true);
      setTimeout(() => setShake(false), 650);
      return;
    }
    accept(intent);
  };

  // 🎈 Real deals near you, shown before signup (no location prompt — only uses GPS if already granted).
  useEffect(() => {
    if (!open) return;
    const miles = (a, b, c, d) => {
      const r = 3958.8, p1 = (a * Math.PI) / 180, p2 = (c * Math.PI) / 180;
      const dp = ((c - a) * Math.PI) / 180, dl = ((d - b) * Math.PI) / 180;
      const h = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
      return Math.round(r * 2 * Math.asin(Math.sqrt(h)) * 10) / 10;
    };
    const load = (lat, lng) =>
      axios.get(`${API}/spotlights`, { params: { lat, lng } })
        .then((r) => {
          const rows = (r.data.spotlights || []).filter((s) => s.offer?.title && s.lat != null);
          rows.sort((a, b) => (b.offer.kind === "public_promo" ? 1 : 0) - (a.offer.kind === "public_promo" ? 1 : 0));
          setGateDeals(rows.slice(0, 3).map((s) => ({
            id: s.id, name: s.name, offer: s.offer.title, mi: miles(lat, lng, s.lat, s.lng),
          })));
        }).catch(() => {});
    const fallback = () => load(40.7127, -74.006);
    try {
      navigator.permissions.query({ name: "geolocation" })
        .then((st) => {
          if (st.state === "granted" && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition((p) => load(p.coords.latitude, p.coords.longitude), fallback, { timeout: 2500 });
          } else fallback();
        }).catch(fallback);
    } catch { fallback(); }
  }, [open]);

  return (
    <AnimatePresence>
      {joinPrompt && !open && (
        <motion.div
          key="join-prompt"
          data-testid="join-prompt"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[6000] bg-slate-950/95 flex items-center justify-center px-4"
        >
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 26, stiffness: 300 }}
            className="w-full max-w-md rounded-3xl border border-sky-400/25 bg-slate-900 shadow-2xl shadow-sky-500/10 p-6 sm:p-8 text-center"
          >
            <p className="font-heading font-black text-3xl text-white tracking-tight">AIR.<span className="text-sky-400">POG</span></p>
            <p className="font-heading font-bold text-lg text-white mt-4">
              {wasPogr ? "Welcome back, POG'R! 👋" : joinPrompt === "hard" ? "Join the People on the Go! 🎯" : "Welcome back, future POG'R! 👋"}
            </p>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">
              {wasPogr
                ? "Your session ended — sign back in to keep collecting POGZ, catching live deals and RSVPing to events."
                : joinPrompt === "hard"
                ? "You've had your free look, POG — People on the Go get the map, the deals and the Play of the Game. Your FREE account takes 10 seconds."
                : "Your FREE account unlocks POGZ collecting, live deal alerts, event RSVPs and rewards. Takes 10 seconds."}
            </p>
            <button
              data-testid="join-prompt-signup"
              onClick={goSignup}
              className="mt-5 w-full py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-400 text-slate-900 font-heading font-black text-sm tracking-wide flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Rocket className="w-4 h-4" /> {wasPogr ? "SIGN ME BACK IN" : "CREATE MY FREE ACCOUNT"}
            </button>
            {joinPrompt === "soft" ? (
              <button
                data-testid="join-prompt-later"
                onClick={() => setJoinPrompt(null)}
                className="mt-2.5 w-full py-2.5 rounded-2xl text-xs font-bold text-slate-400 hover:text-white transition-colors"
              >
                Maybe later →
              </button>
            ) : (
              <p className="text-[10px] text-slate-600 font-semibold mt-3">Free forever for POG'Rs · Merchants head to <a href="/business" className="text-sky-400 underline">airpog.com/business</a></p>
            )}
          </motion.div>
        </motion.div>
      )}
      {open && (
        <motion.div
          key="consent-gate"
          data-testid="consent-gate"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[6000] bg-slate-950/97 flex items-center justify-center px-4 overflow-y-auto"
        >
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: "spring", damping: 26, stiffness: 300, delay: 0.1 }}
            className="w-full max-w-md rounded-3xl border border-sky-400/25 bg-slate-900 shadow-2xl shadow-sky-500/10 p-6 sm:p-8 my-8 text-center"
          >
            <p className="font-heading font-black text-3xl text-white tracking-tight">AIR.<span className="text-sky-400">POG</span></p>
            <p className="text-[11px] font-black tracking-[0.3em] uppercase text-slate-500 mt-1">Just POG it 🎯</p>
            <p data-testid="gate-category-line" className="font-heading font-extrabold text-lg text-white mt-4 leading-snug">
              Live local deals near you, on a map — <span className="text-emerald-300">free</span>.
            </p>
            <p className="font-heading font-bold text-base text-slate-200 mt-3">Welcome, future POG'R! 🚀</p>
            <div className="text-left bg-slate-800/50 border border-slate-700/60 rounded-2xl px-4 py-3.5 mt-3 space-y-1.5" data-testid="triple-pog">
              <p className="text-[12.5px] text-slate-300 leading-snug">We're <span className="font-black text-sky-300">POGs</span> — <span className="italic">People on the Go</span>.</p>
              <p className="text-[12.5px] text-slate-300 leading-snug">The map finds your next <span className="font-black text-sky-300">POG</span> — <span className="italic">Point of Guidance</span> — for local deals.</p>
              <p className="text-[12.5px] text-slate-300 leading-snug">And scoring one? The ultimate <span className="font-black text-amber-300">POG</span> — <span className="italic">Play of the Game</span>. 🎯</p>
            </div>
            <p className="text-sm text-slate-400 mt-3 leading-relaxed">
              One quick thing before you POG:
            </p>
            {gateDeals.length > 0 && (
              <div className="mt-3 text-left" data-testid="gate-deals-strip">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">🎈 Deals near you right now</p>
                <div className="mt-1.5 space-y-1.5">
                  {gateDeals.map((d) => (
                    <div key={d.id} data-testid={`gate-deal-${d.id}`} className="flex items-center gap-2.5 rounded-xl bg-slate-800/60 border border-slate-700/60 px-3 py-2">
                      <BadgePercent className="w-4 h-4 text-emerald-300 shrink-0" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-[12px] font-bold text-white truncate">{d.name}</span>
                        <span className="block text-[11px] text-emerald-300 font-semibold truncate">{d.offer}</span>
                      </span>
                      <span className="text-[10px] font-bold text-slate-400 shrink-0">{d.mi} mi</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <motion.label
              animate={shake ? { x: [0, -8, 8, -6, 6, -3, 3, 0] } : { x: 0 }}
              transition={{ duration: 0.55 }}
              className={`flex items-start gap-3 bg-slate-800/60 border rounded-2xl p-3.5 mt-4 cursor-pointer text-left transition-colors ${shake ? "border-amber-400" : "border-slate-700 hover:border-sky-500/50"}`}
            >
              <input
                data-testid="consent-checkbox"
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 w-5 h-5 accent-sky-500 shrink-0"
              />
              <span className="text-[13px] text-slate-300 leading-snug">
                I have read and agree to the AIR.POG{" "}
                <a data-testid="consent-terms-link" href="/terms" target="_blank" rel="noreferrer" className="text-sky-400 font-bold underline">Terms of Service</a>{" "}
                and{" "}
                <a data-testid="consent-privacy-link" href="/privacy" target="_blank" rel="noreferrer" className="text-sky-400 font-bold underline">Privacy Policy</a>.
              </span>
            </motion.label>
            {hint && !agreed && (
              <p data-testid="consent-hint" className="text-[11px] font-black text-amber-300 mt-1.5 text-left">☝️ Tick the box to continue</p>
            )}
            <button
              data-testid="consent-signup-btn"
              onClick={() => tryAccept("signup")}
              className="mt-4 w-full h-13 py-3.5 rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-400 text-slate-900 font-heading font-black text-sm tracking-wide flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
            >
              <Rocket className="w-4 h-4" /> CREATE MY FREE ACCOUNT
            </button>
            <button
              data-testid="consent-explore-btn"
              onClick={() => tryAccept("explore")}
              className="mt-2.5 w-full py-2.5 rounded-2xl text-xs font-bold text-slate-400 hover:text-white transition-colors"
            >
              Explore first — I'll sign up when I'm ready →
            </button>
            <p data-testid="gate-privacy-line" className="flex items-center justify-center gap-1.5 text-[11px] text-slate-300 font-semibold mt-4">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0" /> We use your location only to show nearby deals. We don't sell your data.
            </p>
            <p className="text-[10px] text-slate-600 font-semibold mt-1.5">
              Your acceptance is securely recorded · v{CONSENT_VERSION}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
